/**
 * Formulário do pedido — os campos da linha da planilha, na mesma ordem.
 * Itens: digita-se como na coluna N ("26x VMP F 490X590 + G1 / 1x G1") e o sistema
 * mostra na hora como entendeu (quantidade · produto · complemento), com a MESMA
 * leitura do importador.
 */
import { createEffect, createResource, createSignal, For, on, Show } from 'solid-js'
import { Button } from '@ui/components/Button'
import { Dialog } from '@ui/components/Dialog'
import { Input } from '@ui/components/Input'
import type { PedidoDetalhe, StatusPedido } from '@shared/types'
import type { PedidoInput } from '@shared/validation'
import { lerItens } from '../../../../../packages/core/services/pedidos/textos'
import { api } from '../../nucleo/cliente'
import { mensagemErro } from '../../lib/erro'
import { lerNumeroBR, numero, paraEntrada, corExibicao } from './formato'

type Texto = Record<
  'stat' | 'cliente' | 'pedido' | 'orderWerc' | 'proposta' | 'dataEntrega' | 'entregou' | 'transportadora'
  | 'valor' | 'aReceber' | 'nf' | 'prevPgmto' | 'ok' | 'itens',
  string
>

const VAZIO: Texto = {
  stat: '', cliente: '', pedido: '', orderWerc: '', proposta: '', dataEntrega: '', entregou: '',
  transportadora: '', valor: '', aReceber: '', nf: '', prevPgmto: '', ok: '', itens: '',
}

export function FormPedido(props: {
  aberto: boolean
  ano: number
  /** null = novo pedido */
  pedido: PedidoDetalhe | null
  status: StatusPedido[] | undefined
  aoFechar: () => void
  aoSalvar: (id: number) => void
}) {
  const [f, setF] = createSignal<Texto>({ ...VAZIO })
  const [statusId, setStatusId] = createSignal<number | null>(1)
  const [erro, setErro] = createSignal<string | null>(null)
  const [salvando, setSalvando] = createSignal(false)

  createEffect(
    on(
      () => [props.aberto, props.pedido] as const,
      ([aberto, d]) => {
        if (!aberto) return
        setErro(null)
        if (!d) {
          setF({ ...VAZIO })
          setStatusId(1)
          return
        }
        setF({
          stat: d.stat ?? '', cliente: d.cliente ?? '', pedido: d.pedido ?? '', orderWerc: d.orderWerc ?? '',
          proposta: d.proposta ?? '', dataEntrega: d.dataEntrega ?? '', entregou: d.entregou ?? '',
          transportadora: d.transportadora ?? '', valor: d.valor !== null ? numero(d.valor) : '',
          aReceber: d.aReceber !== null ? numero(d.aReceber) : '', nf: d.nf ?? '', prevPgmto: d.prevPgmto ?? '',
          ok: d.ok ?? '', itens: d.itens ?? '',
        })
        setStatusId(d.statusId)
      },
    ),
  )

  const campo = (k: keyof Texto) => (e: InputEvent & { currentTarget: HTMLInputElement | HTMLTextAreaElement }) =>
    setF({ ...f(), [k]: e.currentTarget.value })

  // sugestões (as listas próprias do módulo, sem ligação com o núcleo)
  const [clientes] = createResource(() => f().cliente, (t) => api.pedidos.clientes(t))
  const [transportadoras] = createResource(() => f().transportadora, (t) => api.pedidos.transportadoras(t))
  const itensLidos = () => (f().itens.trim() ? lerItens(f().itens) : [])

  const salvar = async (ev: SubmitEvent) => {
    ev.preventDefault()
    setErro(null)
    const v = f()
    const valor = lerNumeroBR(v.valor)
    const aReceber = lerNumeroBR(v.aReceber)
    if (Number.isNaN(valor)) return setErro('Valor inválido. Use números, como 24.000,50.')
    if (Number.isNaN(aReceber)) return setErro('"A receber" inválido. Use números, como 24.000,50.')
    const d = props.pedido
    // Itens não mexidos → mantém o texto e as linhas como estão (inclusive o que veio da planilha).
    const itens =
      d && v.itens === (d.itens ?? '')
        ? paraEntrada(d).itens
        : itensLidos().map((i) => ({ quantidade: i.quantidade, produto: i.produto, complemento: i.complemento }))
    const entrada: PedidoInput = {
      ano: d?.ano ?? props.ano,
      stat: v.stat, cliente: v.cliente, pedido: v.pedido, orderWerc: v.orderWerc, proposta: v.proposta,
      dataEntrega: v.dataEntrega, entregou: v.entregou, transportadora: v.transportadora,
      valor, aReceber, nf: v.nf, prevPgmto: v.prevPgmto, ok: v.ok, itens, statusId: statusId(),
    }
    setSalvando(true)
    try {
      if (d) {
        await api.pedidos.update(d.id, entrada)
        props.aoSalvar(d.id)
      } else {
        const { id } = await api.pedidos.create(entrada)
        props.aoSalvar(id)
      }
      props.aoFechar()
    } catch (e) {
      setErro(mensagemErro(e))
    } finally {
      setSalvando(false)
    }
  }

  const rotulo = 'flex flex-col gap-1 text-xs text-ink-muted'
  const T = (p: { k: keyof Texto; titulo: string; dica?: string; classe?: string; lista?: string; decimal?: boolean }) => (
    <label class={`${rotulo} ${p.classe ?? ''}`}>
      {p.titulo}
      <Input
        value={f()[p.k]}
        onInput={campo(p.k)}
        placeholder={p.dica}
        list={p.lista}
        inputMode={p.decimal ? 'decimal' : undefined}
        autocomplete="off"
      />
    </label>
  )

  return (
    <Dialog open={props.aberto} onOpenChange={(o) => !o && props.aoFechar()} class="w-[min(760px,calc(100vw-32px))]">
      <form onSubmit={salvar} class="flex flex-col gap-4 p-6 max-h-[85dvh] overflow-auto">
        <div class="flex items-baseline gap-3">
          <h2 class="text-lg font-semibold text-ink">{props.pedido ? 'Editar pedido' : `Novo pedido · ${props.ano}`}</h2>
          <Show when={props.pedido?.origemAba}>
            <span class="text-xs text-ink-subtle">veio da planilha, aba {props.pedido!.origemAba}</span>
          </Show>
        </div>

        <fieldset class="flex flex-col gap-1.5">
          <legend class="text-xs text-ink-muted mb-1">Cor (status)</legend>
          <div class="flex flex-wrap gap-1.5">
            <For each={props.status}>
              {(s) => (
                <button
                  type="button"
                  class="flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs text-ink"
                  classList={{ 'border-accent bg-hover font-medium': statusId() === s.id, 'border-edge': statusId() !== s.id }}
                  aria-pressed={statusId() === s.id}
                  onClick={() => setStatusId(s.id)}
                >
                  <span class="size-3 rounded-full border border-black/25" style={{ background: corExibicao(s) }} />
                  {s.nome}
                </button>
              )}
            </For>
          </div>
        </fieldset>

        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <T k="stat" titulo="STAT." dica="estoque, IMP 0076…" />
          <T k="cliente" titulo="Cliente *" classe="col-span-2 sm:col-span-3" lista="ped-clientes" />
          <T k="pedido" titulo="Pedido - dia data" dica="4504358192 - 07.01.2026" classe="col-span-2" />
          <T k="orderWerc" titulo="Order Werc" dica="0011/2026" />
          <T k="proposta" titulo="Proposta" />
          <T k="dataEntrega" titulo="Data entrega" dica="20.01.2026" />
          <T k="entregou" titulo="Entregou" dica="09.02.2026 - OK!" />
          <T k="transportadora" titulo="Transportadora" classe="col-span-2" lista="ped-transportadoras" />
          <T k="valor" titulo="Valor (R$)" decimal />
          <T k="aReceber" titulo="A receber (R$)" decimal />
          <T k="nf" titulo="NF" />
          <T k="prevPgmto" titulo="Prev. pgmto." dica="27.02.2026 - pg" />
          <T k="ok" titulo="OK" dica="PAGO" />
        </div>

        <label class={rotulo}>
          Itens (como na coluna N: separe por " / ")
          <textarea
            class="min-h-16 rounded-md border border-edge bg-input px-2 py-1.5 text-sm text-ink"
            value={f().itens}
            onInput={campo('itens')}
            placeholder="26x VMP F 490X590 + G1 / 1x BFLAP DN 250"
          />
        </label>
        <Show when={itensLidos().length > 0}>
          <div class="rounded-lg border border-edge text-xs">
            <div class="px-3 py-1.5 text-ink-subtle border-b border-edge">Como o sistema entendeu os itens</div>
            <table class="w-full">
              <tbody>
                <For each={itensLidos()}>
                  {(i) => (
                    <tr class="border-b border-edge-muted last:border-0">
                      <td class="px-3 py-1 w-16 text-right tabular-nums">{i.quantidade ?? <span class="text-warning">sem qtd.</span>}</td>
                      <td class="px-3 py-1 text-ink">{i.produto ?? '—'}</td>
                      <td class="px-3 py-1 text-ink-subtle">{i.complemento ?? ''}</td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>

        <datalist id="ped-clientes">
          <For each={clientes.latest ?? []}>{(c) => <option value={c.nome} />}</For>
        </datalist>
        <datalist id="ped-transportadoras">
          <For each={transportadoras.latest ?? []}>{(c) => <option value={c.nome} />}</For>
        </datalist>

        <Show when={erro()}>
          <p class="text-sm text-failure" role="alert">{erro()}</p>
        </Show>
        <div class="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => props.aoFechar()}>Cancelar</Button>
          <Button type="submit" variant="accent" disabled={salvando()}>
            {salvando() ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
